<script setup>

import { ref, computed } from 'vue';

import use$T  from 'cfg/useTranslation';
const $t = use$T();

import Careers from '@Careers/Careers';
import errors from 'cfg/errorBag';

import { useAppStore } from 'store/store';
const { csrf } = useAppStore();


/**
 * Single vacancy driven entirely from the `careers` translation catalog
 * (frontend.jobs.mediabuyer.*). Adding a job = adding a key + an id here.
 */
const jobs = [
  { id: 'mediabuyer',       dept: 'media' },
  { id: 'salesmanager',     dept: 'sales' },
  { id: 'marketingmanager', dept: 'marketing' },
];

const filters = [
  { value: 'all',       label: 'filters.all' },
  { value: 'media',     label: 'filters.media' },
  { value: 'sales',     label: 'filters.sales' },
  { value: 'marketing', label: 'filters.marketing' },
];

const activeFilter = ref('all');

const visibleJobs = computed(()=>
  activeFilter.value === 'all'
    ? jobs
    : jobs.filter((j)=> j.dept === activeFilter.value)
);

const detailsModal = ref(null);
const active       = ref(null);
const cvInput      = ref(null);

const form = ref({ name: '', email: '', message: '' });
const cv   = ref(null);

const submitting = ref(false);
const sent       = ref(false);

const activeJob = computed(()=> active.value ? job(active.value) : null);

function job(id) {
  const base = `jobs.${id}`;
  return {
    id,
    title      : $t(`@${base}.title`),
    salary     : $t(`@${base}.salary`),
    experience : $t(`@${base}.experience`),
    employment : $t(`@${base}.employment`),
    schedule   : $t(`@${base}.schedule`),
    location   : $t(`@${base}.location`),
    intro      : $t(`@${base}.intro`),
    sections   : Object.values($t.map(`@${base}.sections`)).map((s)=> ({
      heading : s.heading,
      items   : Object.values(s.items || {}),
    })),
  };
}

function openJob(id) {
  active.value = id;
  resetForm();
  detailsModal.value.open();
}

function resetForm() {
  errors.clear();
  sent.value = false;
  form.value = { name: '', email: '', message: '' };
  cv.value = null;
  if (cvInput.value) cvInput.value.value = '';
}

function pickFile() {
  cvInput.value?.click();
}

function onFile(event) {
  errors.remove('cv');
  cv.value = event.target.files[0] || null;
}

function clearFile() {
  cv.value = null;
  if (cvInput.value) cvInput.value.value = '';
}

const cvSize = computed(()=> {
  if (!cv.value) return '';
  const kb = cv.value.size / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
});

async function submit() {
  if (submitting.value) return;
  errors.clear();

  const name  = form.value.name.trim();
  const email = form.value.email.trim();

  if (!name)  return errors.add({ field: 'name',  msg: $t('@form.err_name') });
  if (!email) return errors.add({ field: 'email', msg: $t('@form.err_email') });
  if (!cv.value) return errors.add({ field: 'cv', msg: $t('@form.err_cv') });

  const payload = new FormData();
  payload.append('csrf', csrf['careers::apply'] || '');
  payload.append('position', activeJob.value?.title || '');
  payload.append('name', name);
  payload.append('email', email);
  payload.append('message', form.value.message.trim());
  payload.append('cv', cv.value);

  submitting.value = true;

  try {
    const data = await Careers.sendCv(payload);
    if (data && data.success) sent.value = true;
  } catch {
    if (!errors.items.length) errors.add({ field: 'global', msg: $t('@form.err_generic') });
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="jobs-app">

    <div class="jobs-filter">
      <button
        v-for="f in filters"
        :key="f.value"
        type="button"
        class="jobs-filter-btn"
        :class="{ active: activeFilter === f.value }"
        @click="activeFilter = f.value"
      >
        {{ $t('@' + f.label) }}
      </button>
    </div>

    <div class="jobs-list">
      <div v-for="j in visibleJobs" :key="j.id" class="job-row" @click="openJob(j.id)">
        <div class="job-info">
          <div class="job-title">{{ job(j.id).title }}</div>
          <div class="job-tags">
            <span class="job-tag">{{ job(j.id).salary }}</span>
            <span class="job-tag">{{ job(j.id).schedule }}</span>
            <span class="job-tag">{{ job(j.id).location }}</span>
          </div>
        </div>
        <button type="button" class="btn btn-primary job-open">{{ $t('@jobs.view') }}</button>
      </div>
    </div>

    <bmodal ref="detailsModal" size="xlg" :need-footer="false">
      <template #title>{{ activeJob?.title }}</template>
      <template #body>
        <div v-if="activeJob" class="job-detail">

          <div class="job-meta-grid">
            <div><span>{{ $t('@jobs.f_salary') }}</span>{{ activeJob.salary }}</div>
            <div><span>{{ $t('@jobs.f_experience') }}</span>{{ activeJob.experience }}</div>
            <div><span>{{ $t('@jobs.f_employment') }}</span>{{ activeJob.employment }}</div>
            <div><span>{{ $t('@jobs.f_schedule') }}</span>{{ activeJob.schedule }}</div>
          </div>

          <p class="job-intro">{{ activeJob.intro }}</p>

          <div v-for="(sec, i) in activeJob.sections" :key="i" class="job-section">
            <h4>{{ sec.heading }}</h4>
            <ul>
              <li v-for="(item, k) in sec.items" :key="k">{{ item }}</li>
            </ul>
          </div>

          <div class="job-apply">
            <h4>{{ $t('@form.heading') }}</h4>

            <div v-if="sent" class="job-sent">{{ $t('@form.success') }}</div>

            <form v-else @submit.prevent="submit">
              <div v-if="errors.has('global')" class="job-form-err">{{ errors.first('global') }}</div>

              <div class="job-field-row">
                <div class="job-field">
                  <label>{{ $t('@form.name') }}</label>
                  <input v-model="form.name" type="text" :placeholder="$t('@form.name_ph')">
                  <small class="job-field-err">{{ errors.first('name') || '' }}</small>
                </div>

                <div class="job-field">
                  <label>{{ $t('@form.email') }}</label>
                  <input v-model="form.email" type="email" :placeholder="$t('@form.email_ph')">
                  <small class="job-field-err">{{ errors.first('email') || '' }}</small>
                </div>
              </div>

              <div class="job-field">
                <label>{{ $t('@form.message') }}</label>
                <textarea v-model="form.message" rows="5" :placeholder="$t('@form.message_ph')"></textarea>
              </div>

              <div class="job-field">
                <label>{{ $t('@form.cv') }}</label>

                <input
                  ref="cvInput"
                  type="file"
                  class="job-cv-input"
                  accept=".pdf,.doc,.docx,.rtf,.txt"
                  @change="onFile"
                >

                <button
                  v-if="!cv"
                  type="button"
                  class="job-cv-drop"
                  :class="{ invalid: errors.has('cv') }"
                  @click="pickFile"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <span class="job-cv-drop-main">{{ $t('@form.cv_choose') }}</span>
                  <span class="job-cv-drop-hint">{{ $t('@form.cv_hint') }}</span>
                </button>

                <div v-else class="job-cv-file">
                  <svg class="job-cv-file-ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span class="job-cv-file-name">{{ cv.name }}</span>
                  <span class="job-cv-file-size">{{ cvSize }}</span>
                  <button type="button" class="job-cv-file-remove" :aria-label="$t('@form.cv_remove')" @click="clearFile">&times;</button>
                </div>

                <small class="job-field-err">{{ errors.first('cv') || '' }}</small>
              </div>

              <vue-ladda type="submit" data-style="expand-right" class="btn btn-primary job-submit" :loading="submitting">
                {{ $t('@form.submit') }}
              </vue-ladda>
            </form>
          </div>

        </div>
      </template>
    </bmodal>
  </div>
</template>

<style lang="sass" scoped>

.jobs-filter
  display: flex
  gap: 8px
  flex-wrap: wrap
  margin-bottom: 24px

.jobs-filter-btn
  padding: 8px 14px
  border-radius: var(--r-pill, 999px)
  background: var(--paper, #fff)
  border: 1px solid var(--line-2, #E5E7E2)
  font-family: inherit
  font-size: 13px
  font-weight: 600
  color: var(--ink-2, #4a5568)
  cursor: pointer
  transition: all .15s

  &:hover
    border-color: var(--brand-400)

  &.active
    background: var(--ink, #0A1612)
    color: #fff
    border-color: var(--ink, #0A1612)

.jobs-list
  display: flex
  flex-direction: column
  gap: 12px

.job-row
  display: flex
  align-items: center
  justify-content: space-between
  gap: 16px
  padding: 20px 24px
  border: 1px solid var(--line, #E5E7E2)
  border-radius: var(--r-md, 12px)
  background: var(--paper, #fff)
  cursor: pointer
  transition: border-color .15s ease, transform .15s ease

  &:hover
    border-color: var(--brand-400)
    transform: translateX(4px)

.job-title
  font-family: var(--display, sans-serif)
  font-weight: 600
  font-size: 17px
  letter-spacing: -0.01em
  margin-bottom: 8px

.job-tags
  display: flex
  flex-wrap: wrap
  gap: 6px

.job-tag
  font-size: 12px
  color: var(--ink-2, #4a5568)
  background: var(--bg, #f5f6f3)
  border-radius: var(--r-pill, 999px)
  padding: 3px 10px

.job-open
  flex-shrink: 0

.job-detail
  font-size: 15px
  line-height: 1.6
  color: var(--ink-2, #4a5568)

.job-meta-grid
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 10px 20px
  margin-bottom: 20px
  padding-bottom: 20px
  border-bottom: 1px solid var(--line, #E5E7E2)

  div
    font-size: 14px

  span
    display: block
    font-size: 11px
    text-transform: uppercase
    letter-spacing: .06em
    color: var(--muted, #8a94a6)
    margin-bottom: 2px

.job-intro
  margin: 0 0 20px
  font-weight: 500
  color: var(--ink, #0A1612)

.job-section
  margin-bottom: 18px

  h4
    font-size: 15px
    font-weight: 600
    color: var(--ink, #0A1612)
    margin-bottom: 8px

  ul
    margin: 0
    padding-left: 20px

  li
    margin-bottom: 5px

.job-apply
  margin-top: 26px
  padding-top: 22px
  border-top: 1px solid var(--line, #E5E7E2)

  h4
    font-size: 17px
    font-weight: 600
    color: var(--ink, #0A1612)
    margin-bottom: 14px

.job-field-row
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 14px

  .job-field
    margin-bottom: 14px

@media (max-width: 520px)
  .job-field-row
    grid-template-columns: 1fr
    gap: 0

.job-field
  margin-bottom: 14px

  label
    display: block
    font-size: 13px
    font-weight: 600
    color: var(--ink-2, #4a5568)
    margin-bottom: 6px

  input, textarea
    width: 100%
    padding: 11px 14px
    font-family: inherit
    font-size: 14px
    border: 1px solid var(--line-2, #E5E7E2)
    border-radius: var(--r-sm, 8px)
    background: var(--paper, #fff)
    color: var(--ink, #0A1612)

  input:focus, textarea:focus
    outline: none
    border-color: var(--brand-400)
    box-shadow: 0 0 0 3px var(--brand-50)

.job-cv-input
  position: absolute
  width: 1px
  height: 1px
  padding: 0
  margin: -1px
  overflow: hidden
  clip: rect(0 0 0 0)
  border: 0

.job-cv-drop
  width: 100%
  display: flex
  flex-direction: column
  align-items: center
  gap: 4px
  padding: 20px 16px
  border: 1.5px dashed var(--line-2, #E5E7E2)
  border-radius: var(--r-md, 12px)
  background: var(--bg, #f5f6f3)
  color: var(--ink-2, #4a5568)
  font-family: inherit
  cursor: pointer
  transition: border-color .15s ease, background .15s ease, color .15s ease

  &:hover
    border-color: var(--brand-400)
    background: var(--brand-50)
    color: var(--brand-700)

  &.invalid
    border-color: #dc2626

  svg
    color: var(--muted, #8a94a6)

  &:hover svg
    color: var(--brand-500)

.job-cv-drop-main
  font-size: 14px
  font-weight: 600

.job-cv-drop-hint
  font-size: 12px
  color: var(--muted, #8a94a6)

.job-cv-file
  display: flex
  align-items: center
  gap: 10px
  padding: 12px 14px
  border: 1px solid var(--line-2, #E5E7E2)
  border-radius: var(--r-md, 12px)
  background: var(--paper, #fff)

.job-cv-file-ic
  color: var(--brand-500)
  flex-shrink: 0

.job-cv-file-name
  font-size: 14px
  font-weight: 600
  color: var(--ink, #0A1612)
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

.job-cv-file-size
  font-size: 12px
  color: var(--muted, #8a94a6)
  flex-shrink: 0
  margin-left: auto

.job-cv-file-remove
  flex-shrink: 0
  width: 26px
  height: 26px
  display: grid
  place-items: center
  border: 0
  border-radius: 50%
  background: var(--bg, #f5f6f3)
  color: var(--muted, #6B7770)
  font-size: 18px
  line-height: 1
  cursor: pointer
  transition: background .15s ease, color .15s ease

  &:hover
    background: #fee2e2
    color: #dc2626

.job-hint
  display: block
  font-size: 12px
  color: var(--muted, #8a94a6)
  margin-top: 4px

.job-field-err
  display: block
  color: #dc2626
  font-size: 12px
  margin-top: 4px

  &:empty
    display: none

.job-form-err
  background: #fef2f2
  border: 1px solid #fecaca
  color: #b91c1c
  border-radius: 8px
  padding: 10px 12px
  font-size: 13px
  margin-bottom: 12px

.job-submit
  width: 100%
  justify-content: center
  margin-top: 6px
  padding: 13px

.job-sent
  background: var(--accent-50, #eafaf0)
  border: 1px solid var(--accent-100, #bfe9cf)
  color: #146c43
  border-radius: var(--r-md, 12px)
  padding: 16px
  font-size: 14px
  text-align: center

</style>
